import { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, MapPin, Phone } from "lucide-react";
import { NAV_LINKS, SITE } from "@/data/site";

export default function Nav() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 64);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled;

  const barBg = solid
    ? "bg-ink/92 backdrop-blur-md border-b border-gold/25"
    : "bg-gradient-to-b from-ink/70 to-transparent border-b border-transparent";
  const linkColor = "text-cream";

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${barBg}`}
        data-testid="main-nav"
      >
        <div className="u-container flex items-center justify-between h-[76px] md:h-[88px]">
          <nav className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `u-eyebrow link-line ${linkColor} ${isActive ? "opacity-100" : "opacity-60 hover:opacity-100"} transition-opacity`
                }
              >
                {({ isActive }) => (
                  <span className="inline-flex items-center gap-2">
                    {isActive && <span className="inline-block w-1 h-1 rounded-full bg-terracotta" />}
                    {l.label}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 ml-auto md:ml-0">
            <Link
              to="/menu"
              data-testid="nav-cta-menu"
              className="hidden sm:inline-flex u-eyebrow px-5 py-2.5 rounded-full border border-cream/30 text-cream hover:bg-cream/10 hover:border-cream/50 transition-colors duration-300 text-xs"
            >
              Explore Menu
            </Link>
            <Link
              to="/contact#enquire"
              data-testid="nav-cta-reserve"
              className="hidden sm:inline-flex u-eyebrow px-5 py-2.5 rounded-full border border-gold text-gold hover:bg-gold hover:text-ink transition-colors duration-500 text-xs"
            >
              Reserve Table
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
              className={`md:hidden flex items-center justify-center w-10 h-10 rounded-full border border-gold/30 text-gold bg-ink/50 backdrop-blur-sm hover:bg-gold hover:text-ink transition-all`}
            >
              <Menu strokeWidth={1.5} size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Premium Mobile Menu Overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] bg-gradient-to-b from-ink via-charcoal to-ink text-cream flex flex-col overflow-y-auto no-scrollbar"
            initial={{ opacity: 0, y: "-10%" }}
            animate={{ opacity: 1, y: "0%" }}
            exit={{ opacity: 0, y: "-10%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="mobile-menu-panel"
          >
            {/* Header */}
            <div className="u-container flex items-center justify-between h-[76px] border-b border-gold/20 shrink-0">
              <Link to="/" onClick={() => setOpen(false)} className="h-9 w-auto">
                <img src="/images/logo_new_white_clean.png" alt="Chef Jomon's #VEEDU" className="h-full w-auto object-contain" />
              </Link>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold hover:text-ink transition-colors"
              >
                <X strokeWidth={1.5} size={20} />
              </button>
            </div>

            {/* Navigation links */}
            <div className="flex-1 flex flex-col justify-center u-container py-8 space-y-6">
              <div className="space-y-4">
                {NAV_LINKS.map((l, i) => {
                  const isActive = pathname === l.to;
                  return (
                    <motion.div
                      key={l.to}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <Link
                        to={l.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="group flex items-center justify-between py-2 border-b border-cream/10"
                      >
                        <div className="flex items-center gap-4">
                          <span className="font-display italic text-gold/60 text-lg">
                            0{i + 1}
                          </span>
                          <span className={`font-display text-4xl leading-tight transition-colors ${
                            isActive ? "text-gold" : "text-cream group-hover:text-gold"
                          }`}>
                            {l.label}
                          </span>
                        </div>
                        {isActive ? (
                          <span className="w-2 h-2 rounded-full bg-gold shadow-[0_0_10px_#C5A059]" />
                        ) : (
                          <ArrowUpRight size={18} className="text-cream/30 group-hover:text-gold transition-colors" />
                        )}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="grid grid-cols-2 gap-3 pt-4"
              >
                <Link
                  to="/menu"
                  onClick={() => setOpen(false)}
                  className="btn-veedu border-cream/30 text-cream hover:bg-cream/10 text-xs py-3 text-center flex items-center justify-center gap-1.5"
                >
                  Explore Menu <ArrowUpRight size={14} />
                </Link>
                <Link
                  to="/contact#enquire"
                  onClick={() => setOpen(false)}
                  className="btn-veedu border-gold text-gold hover:bg-gold hover:text-ink text-xs py-3 text-center flex items-center justify-center gap-1.5"
                >
                  Reserve Table <ArrowUpRight size={14} />
                </Link>
              </motion.div>
            </div>

            {/* Footer details inside overlay */}
            <div className="u-container pb-8 pt-4 border-t border-gold/15 shrink-0 space-y-3">
              <p className="font-script text-gold text-xl italic text-center">
                Come and listen to the food tales of Kerala
              </p>
              <div className="flex flex-wrap items-center justify-between text-xs font-body font-light text-cream/50 gap-2 pt-1">
                <span className="flex items-center gap-1.5">
                  <MapPin size={13} className="text-terracotta" /> Hornchurch, Essex
                </span>
                <a href={`tel:${SITE.phones.restaurant.replace(/[^+\d]/g, "")}`} className="flex items-center gap-1.5 text-cream/70 hover:text-gold">
                  <Phone size={13} className="text-gold" /> {SITE.phones.restaurant}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
